/* istanbul ignore file */
import { SystemStyleObject, createContext } from '@chakra-ui/react';
import React, { ChangeEventHandler, Dispatch, FunctionComponent, JSX, SetStateAction, useEffect } from 'react';
import { IconProps } from 'react-feather';
import { UseFormReturn } from 'react-hook-form';
import { TableRoot } from '.';
import { OrderByEnum } from '@/constants/sort';
import { useMultiStyleConfig } from '@chakra-ui/system';
import { LocalStorageKeys } from '../../util/local-storage';

interface RowAction<RowType> {
  label: string;
  callback: (rowData: RowType) => void;
  icon?: React.ReactElement;
  isDisabled?: boolean | ((rowData: RowType) => boolean);
  color?: string;
}

export type OrderBy = {
  attribute?: string | null;
  order?: OrderByEnum | null;
};

export type PageInfo = {
  numItemsPerPage?: number | null;
  orderBy?: Array<OrderBy>;
  page?: number | null;
  totalNumItems?: number | null;
  totalNumPages?: number | null;
};

export type OrderByInput = {
  attribute: string;
  order: OrderByEnum;
};

export type PageInfoInput = {
  numItemsPerPage: number;
  orderBy?: OrderByInput[] | null;
  page: number;
};

export type PageInfoReduced = {
  page: number;
  numItemsPerPage: number;
  nextPageEnabled: boolean;
  totalPages: number;
  totalRecords: number;
};

export type ColumnStyleDefinition = {
  common?: SystemStyleObject;
  header?: SystemStyleObject;
  cell?: SystemStyleObject;
};

export interface DataTableProps<RowType extends object, FilterType extends object> {
  header: string;
  tableData: RowType[];
  isLoading?: boolean;
  displayKeyLabelMap: { [key: string]: string | React.ReactElement };
  fieldRenderOverrides?: {
    [key: string]: ({ row, key, index }: { row: RowType; key: string; index: number }) => React.FC | JSX.Element;
  };
  showVisibleFieldsToggle?: boolean;
  defaultVisibleKeys?: string[];
  stickyLeftColumnKeys?: string[];
  stickyRightColumnKeys?: string[];
  rowActions?: Array<RowAction<RowType>> | ((row: RowType) => Array<RowAction<RowType>>) | null;
  variant?: 'standard' | 'inline' | 'fixed-column';

  pageInfo?: PageInfo;
  pageInfoInput?: PageInfoInput;
  setPageInfoInput?: Dispatch<SetStateAction<object>>;

  pageInfoReduced?: PageInfoReduced;
  setPageInfoReduced?: Dispatch<SetStateAction<PageInfoReduced>>;

  filterOptions?: React.FC[] | JSX.Element[];
  filterForm?: UseFormReturn<FilterType>;
  filterDisplayKeys?: { [key: string]: string };

  showSearch?: boolean;
  onSearch?: ChangeEventHandler<HTMLInputElement>;
  sortableKeys?: string[];
  centerAlignedKeys?: string[];
  rightAlignedKeys?: string[];
  bulkActions?: Array<{
    label: string;
    callback: () => void;
    icon?: React.ReactElement | FunctionComponent<IconProps>;
  }>;
  selectedRows?: string[];
  setSelectedRows?: Dispatch<string[]>;
  selectedRowsKey?: string;
  paddingCell?: string;

  // SATIVA-1 EPIC
  showFieldVisibilitySlider?: boolean;

  displayInCard?: boolean;
  verticalBordersProp?: string;
  borderStyles?: string;
  disableTableFooterActions?: boolean;
  showTableFooter?: boolean;
  configPersistenceId?: string;
  highlightedRows?: string[];
  columnStyleOverride?: {
    generalCellStyle?: ColumnStyleDefinition;
    cellStylePerColumn?: { [key: string]: ColumnStyleDefinition };
  };
  onRowClick?: (row: RowType, e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  customTableRowActions?: ({ row }: { row: RowType }) => JSX.Element;
}

export interface BulkAction {
  label: string;
  mutationResolver: () => void;
}

type DataTablePropsContextType<RowType extends object, FilterType extends object> = DataTableProps<
  RowType,
  FilterType
> & {
  styles: Record<string, SystemStyleObject>;
  visibleKeys?: string[];
};

type DataTableContextTypes<RowType extends object, FilterType extends object> = {
  tableProps: DataTablePropsContextType<RowType, FilterType>;
  setDataTableProps: Dispatch<DataTablePropsContextType<RowType, FilterType>>;
};

type DataTableConfigStorageType = {
  visibleKeys?: string[];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DataTableContext = React.createContext<DataTableContextTypes<any, any>>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  {} as DataTableContextTypes<any, any>,
);

function getTableConfigFromStorage(id: string): DataTableConfigStorageType | null {
  if (!id) {
    return null;
  }

  const config = localStorage.getItem(LocalStorageKeys.DATA_TABLE_CONFIGS);
  return config ? (JSON.parse(config) as { [key: string]: object })[id] : null;
}

function updateTableConfigToStorage(storageKey: string, visibleKeys?: string[]): void {
  if (!storageKey || !visibleKeys?.length) {
    return;
  }

  const existingConfigString = localStorage.getItem(LocalStorageKeys.DATA_TABLE_CONFIGS);
  const existingConfig = existingConfigString ? (JSON.parse(existingConfigString) as { [key: string]: object }) : {};
  const newConfig = {
    ...existingConfig,
    [storageKey]: {
      visibleKeys: visibleKeys,
    },
  };

  localStorage.setItem(LocalStorageKeys.DATA_TABLE_CONFIGS, JSON.stringify(newConfig));
}

const [StylesProvider] = createContext({
  name: 'DataTable',
  strict: true,
});

export const DataTable = <RowType extends object, FilterType extends object>(
  props: DataTableProps<RowType, FilterType>,
) => {
  const { defaultVisibleKeys, displayKeyLabelMap, showVisibleFieldsToggle = true, configPersistenceId = '' } = props;

  const styles = useMultiStyleConfig('DataTable', { variant: 'standard' });

  const [tableProps, setDataTableProps] = React.useState<DataTablePropsContextType<RowType, FilterType>>({
    ...props,
    showVisibleFieldsToggle,
    styles: styles as Record<string, SystemStyleObject>,
    visibleKeys: defaultVisibleKeys,
  });

  const updateDataTableProps = (tableProps: DataTablePropsContextType<RowType, FilterType>) => {
    setDataTableProps(tableProps);
    updateTableConfigToStorage(configPersistenceId, tableProps.visibleKeys);
  };

  // Update if incoming props change
  useEffect(() => {
    const savedConfig = getTableConfigFromStorage(configPersistenceId);

    const newTableProps = {
      ...tableProps,
      ...props,
      visibleKeys: savedConfig?.visibleKeys || defaultVisibleKeys || Object.keys(displayKeyLabelMap),
    };
    setDataTableProps(newTableProps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  return (
    <DataTableContext.Provider value={{ tableProps, setDataTableProps: updateDataTableProps }}>
      <StylesProvider value={styles}>
        <TableRoot />
      </StylesProvider>
    </DataTableContext.Provider>
  );
};
